import { CarouselContainer } from "@breuni-ui/carousel";
import { safeDefine } from "@breuni-ui/safe-define";
import {
  trackBestandteilClick,
  trackBestandteileImpression,
  trackOutfitClick,
  trackOutfitsImpression,
} from "../tracking/cube/tracking";
import type KombiModulCarousel from "./KombiModulCarousel";
import type {
  BestandteilTrackingData,
  OutfitClickDetails,
} from "./KombiModulCarousel";
import { debounce } from "../utils/debounce";

export type LoadBestandteileDetails = {
  farbId: string;
  relativePath: string;
};

const dataCurrency = "data-currency";
const dataHeadlineDE = "data-headline-de";
const recoKombiModulCarousel = "reco-kombi-modul-carousel";
const slideIndexKey = "data-slide-index";

export default class KombiModulCarouselContainer extends CarouselContainer {
  private intersectionObserver: IntersectionObserver | null = null;
  private readonly alreadySeenKombiModuleCombinations = new Set<string>();
  private carousel: KombiModulCarousel | null | undefined;
  private readonly abortController: AbortController = new AbortController();

  public constructor() {
    super();

    window.addEventListener(
      "pageshow",
      (event: PageTransitionEvent) => {
        if (event.persisted && this.intersectionObserver !== null) {
          this.setupTracking();
        }
      },
      {
        signal: this.abortController.signal,
      },
    );
  }

  public connectedCallback(): void {
    super.connectedCallback();
    this.setupTracking();
    this.carousel = this.querySelector<KombiModulCarousel>(
      recoKombiModulCarousel,
    );
    this.setupEventHandling();
  }

  public disconnectedCallback(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.abortController.abort();
  }

  private setupTracking(): void {
    const isOutfitSlider = this.getAttribute(dataHeadlineDE) === null;
    this.observeSlides(isOutfitSlider);
  }

  private observeSlides(isOutfitSlider: boolean): void {
    if (this.intersectionObserver !== null) {
      this.intersectionObserver.disconnect();
    }
    const debouncedTrackImpression = debounce(() => {
      this.trackImpression();
    }, 300);

    this.alreadySeenKombiModuleCombinations.clear();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            debouncedTrackImpression();
          }
        });
      },
      { threshold: [0.49] },
    );
    const slideSelector = isOutfitSlider
      ? '[data-id="open-bestandteile"]'
      : '[data-typ="bestandteil"]';
    this.querySelectorAll(slideSelector).forEach((bestandteil) => {
      this.intersectionObserver?.observe(bestandteil);
    });
  }

  private setupEventHandling(): void {
    if (this.carousel !== null && typeof this.carousel !== "undefined") {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      this.carousel.addEventListener("outfit:click", ((
        ev: CustomEvent<OutfitClickDetails>,
      ) => {
        this.handleOutfitClick(ev);
      }) as EventListener);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      this.carousel.addEventListener("bestandteil:click", ((
        ev: CustomEvent<BestandteilTrackingData>,
      ) => {
        trackBestandteilClick(ev.detail, this.getAttribute(dataCurrency) ?? "");
      }) as EventListener);
    }
  }

  private handleOutfitClick(ev: CustomEvent<OutfitClickDetails>): void {
    trackOutfitClick(ev.detail.index, this.getAttribute(dataCurrency) ?? "");
    this.dispatchEvent(
      new CustomEvent<LoadBestandteileDetails>("load:bestandteile", {
        detail: {
          farbId: ev.detail.farbId,
          relativePath: ev.detail.relativePath,
        },
      }),
    );
  }

  private isAlreadyTracked(key: string): boolean {
    return this.alreadySeenKombiModuleCombinations.has(key);
  }

  private markAsTracked(key: string): void {
    this.alreadySeenKombiModuleCombinations.add(key);
  }

  private trackBestandteileImpression(
    headlineDE: string,
    currency: string,
    visibleIndices: string[],
  ): void {
    const bestandteileData =
      this.querySelector<KombiModulCarousel>(
        recoKombiModulCarousel,
      )?.getBestandteileTrackingData() ?? [];
    const visibleBestandteileData = bestandteileData.filter(({ position }) =>
      visibleIndices.includes(`${position}`),
    );

    trackBestandteileImpression(headlineDE, currency, visibleBestandteileData);
  }

  private trackImpression(): void {
    const visibleIndices = this.getVisibleIndices();
    const key = visibleIndices.join("|");
    if (this.isAlreadyTracked(key)) {
      return;
    }
    const headlineDE = this.getAttribute(dataHeadlineDE);
    const currency = this.getAttribute(dataCurrency) ?? "";
    if (headlineDE === null) {
      trackOutfitsImpression(currency);
    } else {
      this.trackBestandteileImpression(headlineDE, currency, visibleIndices);
    }
    this.markAsTracked(key);
  }

  private getVisibleIndices(): string[] {
    return [...this.querySelectorAll<HTMLElement>('[data-visible="true"]')]
      .map((produktEl) => produktEl.getAttribute(slideIndexKey))
      .filter((pos): pos is string => pos !== null);
  }
}

safeDefine("reco-kombi-modul-carousel-container", KombiModulCarouselContainer);
